import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV_LINKS } from "../data";
import { Logo, CTAButton } from "./UI";

// Pages that open with a dark/black hero — nav should start in dark mode
const DARK_HERO_PAGES = ["/", "/services", "/about", "/contact", "/blog"];

export default function Nav() {
  const [pastHero, setPastHero] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const hasDarkHero = DARK_HERO_PAGES.includes(location.pathname);

  useEffect(() => {
    const onScroll = () => {
      // On home, track the #hero element's bottom
      // On other pages with dark heroes, use scroll position (hero is typically ~60-80vh tall)
      const heroEl = document.getElementById("hero");
      if (heroEl) {
        const heroBottom = heroEl.getBoundingClientRect().bottom;
        setPastHero(heroBottom <= 80);
      } else if (hasDarkHero) {
        // For non-home dark-hero pages, treat first ~60vh as "in hero"
        setPastHero(window.scrollY > window.innerHeight * 0.5);
      } else {
        setPastHero(true);
      }
      setScrolled(window.scrollY > 30);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname, hasDarkHero]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const navClass = [
    "nav",
    scrolled ? "scrolled" : "",
    !hasDarkHero || pastHero ? "nav-light" : "nav-dark",
  ].filter(Boolean).join(" ");

  return (
    <>
      <header className={navClass}>
        <div className="container nav-inner">
          <Link to="/" className="nav-logo" aria-label="OVARD Digital — home">
            <Logo height={44} />
          </Link>
          <nav className="nav-links" aria-label="Primary">
            {NAV_LINKS.map((l) => {
              if (l.id === "home") {
                return <Link key={l.id} to="/" className="nav-link">{l.label}</Link>;
              }
              if (l.id === "services") {
                return <Link key={l.id} to="/services" className="nav-link">{l.label}</Link>;
              }
              if (l.id === "about") {
                return <Link key={l.id} to="/about" className="nav-link">{l.label}</Link>;
              }
              if (l.id === "blog") {
                return <Link key={l.id} to="/blog" className="nav-link">{l.label}</Link>;
              }
              if (l.id === "contact") {
                return <Link key={l.id} to="/contact" className="nav-link">{l.label}</Link>;
              }
              return (
                <a key={l.id} href={isHome ? "#" + l.id : "/#" + l.id} className="nav-link">
                  {l.label}
                </a>
              );
            })}
          </nav>
          <div className="nav-right">
            <CTAButton solid={false} href="https://wa.me/917975034116" target="_blank" rel="noopener noreferrer">Get Started</CTAButton>
            <button
              className="hamburger"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
            >
              <span /><span />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        className={"mobile-menu" + (open ? " open" : "")}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <button
          className="mobile-menu-close"
          onClick={() => setOpen(false)}
          aria-label="Close menu"
        >
          Close ✕
        </button>
        <div className="mobile-links">
          {NAV_LINKS.map((l) => {
            if (l.id === "home") {
              return <Link key={l.id} to="/" onClick={() => setOpen(false)}>{l.label}</Link>;
            }
            if (l.id === "services") {
              return <Link key={l.id} to="/services" onClick={() => setOpen(false)}>{l.label}</Link>;
            }
            if (l.id === "about") {
              return <Link key={l.id} to="/about" onClick={() => setOpen(false)}>{l.label}</Link>;
            }
            if (l.id === "blog") {
              return <Link key={l.id} to="/blog" onClick={() => setOpen(false)}>{l.label}</Link>;
            }
            if (l.id === "contact") {
              return <Link key={l.id} to="/contact" onClick={() => setOpen(false)}>{l.label}</Link>;
            }
            return (
              <a key={l.id} href={isHome ? "#" + l.id : "/#" + l.id} onClick={() => setOpen(false)}>
                {l.label}
              </a>
            );
          })}
        </div>
        <CTAButton solid href="https://wa.me/917975034116" target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
          Get Started
        </CTAButton>
      </div>
    </>
  );
}
